/**
 * You against the computer, on one phone, with no network at all.
 *
 * The engine runs right here in the tab. That is safe for exactly the same
 * reason it is safe on the server: the `Table` is handed `viewFor(state, 'you')`
 * and never the state itself, so the component tree genuinely does not contain
 * the contents of Avalon or anyone else's hand. A devtools breakpoint would find
 * them on the module's state object — this mode is a solitaire against
 * opponents who are not trying to cheat, and hiding the deck from its own player
 * beyond that is not a threat model worth contorting for.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { botMove } from '@shared/bot';
import {
  applyAction,
  newGame,
  viewFor,
  RuleError,
  type GameAction,
  type GameState,
  type SetupPlayer,
} from '@shared/engine';
import { botDelayFor } from '@shared/protocol';
import { Table } from '../game/Table';
import { Toast } from '../ui/shell';

const BOT_NAMES = ['Sir Kay', 'Dame Ragnelle', 'Bedivere', 'Elaine', 'Sir Bors', 'Nimue', 'Sir Lucan'];

export function BotGame({ name, bots, onExit }: { name: string; bots: number; onExit: () => void }) {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const [error, setError] = useState<string | null>(null);
  const stateRef = useRef<GameState | null>(null);

  if (!stateRef.current) stateRef.current = deal(name, bots);

  const act = useCallback((action: GameAction) => {
    const s = stateRef.current;
    if (!s) return;
    try {
      applyAction(s, 'you', action);
    } catch (err) {
      // The UI should never offer an illegal move, so this is a bug rather than
      // a misuse — but a wedged table with no explanation is worse than a toast.
      setError(err instanceof RuleError ? err.message : 'That move did not take.');
      return;
    }
    bump();
  }, []);

  const again = useCallback(() => {
    stateRef.current = deal(name, bots);
    bump();
  }, [name, bots]);

  // The bot loop. One move per tick, paced, so a table of seven does not resolve
  // an entire draw step between two frames.
  const state = stateRef.current;
  useEffect(() => {
    const s = stateRef.current;
    if (!s || s.phase === 'over') return;
    const candidates = s.pending ? [s.pending.playerId] : s.players.map((p) => p.id);
    const mover = candidates.find((id) => {
      const p = s.players.find((x) => x.id === id);
      return p?.isBot && botMove(viewFor(s, id)) !== null;
    });
    if (!mover) return;

    const id = window.setTimeout(() => {
      const live = stateRef.current;
      if (!live) return;
      const move = botMove(viewFor(live, mover));
      if (!move) return;
      try {
        applyAction(live, mover, move);
      } catch (err) {
        console.error('[excalibur] bot played illegally:', err);
        return;
      }
      bump();
    }, botDelayFor(s.phase));
    return () => window.clearTimeout(id);
  });

  if (!state) return null;

  return (
    <>
      <Table
        view={viewFor(state, 'you')}
        act={act}
        onExit={onExit}
        endActions={
          <button type="button" className="btn" onClick={again}>
            Deal again
          </button>
        }
      />
      <Toast text={error} onDone={() => setError(null)} />
    </>
  );
}

function deal(name: string, bots: number): GameState {
  const setup: SetupPlayer[] = [
    { id: 'you', name: name || 'You', isBot: false },
    ...Array.from({ length: bots }, (_, i) => ({
      id: `bot${i + 1}`,
      name: BOT_NAMES[i % BOT_NAMES.length]!,
      isBot: true,
    })),
  ];
  // `crypto.getRandomValues` rather than `Math.random`: a predictable deal tells
  // you where Excalibur is, and V8's PRNG state is recoverable from its outputs.
  const seed = crypto.getRandomValues(new Uint32Array(1))[0]!;
  return newGame(setup, seed);
}
